/* Cart drawer express checkout (snippet cart-drawer): Shopify renders its express-checkout buttons inside
   <shopify-accelerated-checkout-cart>, one child per wallet with a slot like "button-ApplePay". This shows just one:
   Apple Pay, Google Pay or PayPal, the first of those this device offers, or else the first button Shopify shows.
   The wallets load one by one, so it waits until they're all drawn (or a short time), then picks.
   Shopify stacks the buttons in rows and keeps a row for each even when one is hidden, so instead of hiding the others,
   the block becomes a one-button window lined up on the picked button, and the others are made inert. */
(() => {
  if (customElements.get('ff-wallet')) return;

  const PREFER = ['ApplePay', 'GooglePay', 'PayPal'];
  const WAIT_MS = 3000; // longest wait for a wallet that hasn't drawn yet
  const GIVE_UP_MS = 8000; // no wallet at all by then: hide the block (and its "or")

  const wallet = (el) => el.slot.replace(/^button-/, '');

  class FFWallet extends HTMLElement {
    connectedCallback() {
      this.started = performance.now();
      this.check = this.check.bind(this);
      this.mo = new MutationObserver(() => this.queue());
      this.mo.observe(this, { childList: true, subtree: true });
      this.timer = setInterval(this.check, 200);
      this.queue();
    }
    disconnectedCallback() {
      this.stop();
      this.ro?.disconnect();
    }
    stop() {
      this.mo?.disconnect();
      clearInterval(this.timer);
    }
    queue() {
      if (this.queued) return;
      this.queued = true;
      requestAnimationFrame(() => {
        this.queued = false;
        this.check();
      });
    }
    check() {
      const host = this.querySelector('shopify-accelerated-checkout-cart');
      const buttons = host ? [...host.children].filter((el) => /^button-/.test(el.slot || '')) : [];
      const drawn = buttons.filter((el) => el.getBoundingClientRect().height > 0);
      const waited = performance.now() - this.started;
      const best = PREFER.map((name) => drawn.find((el) => wallet(el) === name)).find(Boolean);
      const allDrawn = buttons.length > 0 && drawn.length === buttons.length;

      // Apple Pay can't be beaten; otherwise wait for the rest to draw, up to WAIT_MS
      const done = (best && wallet(best) === PREFER[0]) || allDrawn || (drawn.length && waited > WAIT_MS);
      if (done) return this.pick(best || drawn[0]);
      if (waited > GIVE_UP_MS) {
        this.classList.add('is-none');
        this.stop();
      }
    }
    pick(el) {
      this.stop();
      this.keep = el;
      this.host = el.parentElement;
      el.setAttribute('data-ff-keep', '');
      [...this.host.children].forEach((o) => { if (o !== el && o.slot) o.inert = true; });
      this.dataset.wallet = wallet(el);
      this.place();
      this.classList.add('is-picked');
      // keep the window on the button if the layout changes (drawer width, fonts, a wallet redrawing)
      this.ro = new ResizeObserver(() => this.place());
      this.ro.observe(this.host);
      this.ro.observe(el);
    }
    place() {
      const top = this.keep.getBoundingClientRect().top - this.host.getBoundingClientRect().top;
      const h = this.keep.getBoundingClientRect().height;
      if (!h) return;
      this.style.setProperty('--ff-wallet-top', `${Math.round(top)}px`);
      this.style.setProperty('--ff-wallet-h', `${Math.round(h)}px`);
    }
  }
  customElements.define('ff-wallet', FFWallet);
})();
